import { io, type Socket } from 'socket.io-client';
import { env } from '@/lib/env';

// Retry forever with capped exponential backoff (LIL-440). A fixed attempt
// cap meant a transient BE outage at page load left the socket dead for the
// whole session — automation progress then only arrived via HTTP polling.
const RECONNECT_DELAY_MS = 1_000;
const RECONNECT_DELAY_MAX_MS = 30_000;

export interface SocketCallbacks {
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onError?: (msg: string) => void;
}

class SocketService {
  private static instance: SocketService;
  private socket: Socket | null = null;

  private constructor() {}

  static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  private consumerCount = 0;

  addConsumer(): void {
    this.consumerCount++;
  }

  removeConsumer(): void {
    this.consumerCount = Math.max(0, this.consumerCount - 1);
    if (this.consumerCount === 0) {
      this.disconnect();
    }
  }

  connect(token: string, callbacks?: SocketCallbacks): void {
    if (this.socket?.connected) return;

    if (!env.SOCKET_URL) {
      console.warn('[SocketService] SOCKET_URL is not configured, skipping connection');
      return;
    }

    // Clean up any existing disconnected socket before creating a new one
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }

    this.socket = io(env.SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: Infinity,
      reconnectionDelay: RECONNECT_DELAY_MS,
      reconnectionDelayMax: RECONNECT_DELAY_MAX_MS,
      randomizationFactor: 0.5,
    });

    this.socket.on('connect', () => {
      callbacks?.onConnect?.();
    });

    this.socket.on('disconnect', (reason: string) => {
      callbacks?.onDisconnect?.(reason);
    });

    this.socket.on('connect_error', (error: Error) => {
      callbacks?.onError?.(error.message);
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  on<T = unknown>(event: string, callback: (data: T) => void): void {
    this.socket?.on(event, callback as (...args: unknown[]) => void);
  }

  off<T = unknown>(event: string, callback?: (data: T) => void): void {
    this.socket?.off(event, callback as ((...args: unknown[]) => void) | undefined);
  }

  emit(event: string, data?: unknown): void {
    if (!this.socket?.connected) {
      console.warn(`[SocketService] Cannot emit "${event}" — socket is not connected`);
      return;
    }
    this.socket.emit(event, data);
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

export const socketService = SocketService.getInstance();
